import {
  CLEAR_FILTER_TOKEN,
  CreatedByKey,
  DisplayCategoryKey,
  ExtensionKey,
  FILTER_CONCATENATE_TOKEN,
  JobTypeKey,
  LikedKey,
  ProductWeights,
  PromptTabs,
  WorkgroupKey,
  buildFilters,
  isFilterEnabled,
  type Filter,
  type FilterName,
  type FilterOption,
  type ProductType,
} from "@1js/pl-card-components";
import type {
  Group,
  MicrosoftPrompt,
  Prompt,
  Status,
  UserExtensionPrompt,
  UserPrompt,
  WorkgroupMicrosoftPrompt,
  WorkgroupUserPrompt,
} from "@1js/pl-types";
import {
  isExtensionPrompt,
  isLikedPrompt,
  isMicrosoftPrompt,
  isNonSharedUserPrompt,
  isTenantPrompt,
  isUserPrompt,
  isWorkgroupPrompt,
} from "@1js/pl-utils";
import type { IPublicClientApplication } from "@azure/msal-browser";
import { Avatar } from "@fluentui/react-components";
import React, { useEffect, useReducer } from "react";
import { useSearchParams } from "react-router-dom";
import { deserializeFilters } from "../../../components/AllPrompts/AllPrompts.web.data";
import type { WebPrompt } from "../../../components/data";
import type { PromptReference } from "../../../services/prompt-client.web";
import { IsTeamsTab, TeamWorkGroupId, type SiteSetting } from "../../../types";
import { sendClientLayoutEvent } from "../../../utils/Telemetry";
import usePageSize from "./AllPromptsLibraryPageSize.web";

export type PromptLibraryState = {
  allPrompts: Prompt[];
  allFilteredPrompts: Prompt[];
  displayedPrompts: Prompt[];
  moreLikeThis: WebPrompt[];
  selectedFacets: Record<string, string[]>;
  selectedWorkgroupFacets: Record<string, string[]>;
  initSearchParams: URLSearchParams;
  filters: Filter[];
  workgroupFilters: Filter[];
  status: Status;
  hasMore?: boolean;
  selectedTab: PromptTabs;
  selectedNavItem?: PromptTabs;
  hasBookmarkedPrompts?: boolean;
  groups?: Group[];
  groupPromptStatus: Status;
  groupPrompts: Record<
    string,
    (WorkgroupMicrosoftPrompt | WorkgroupUserPrompt)[]
  >;
  displayedGroupPrompts: Prompt[];
  groupHasPromptsStatus: Status;
  groupMemberStatus: Status;
  groupsHasPrompts?: Group[];
  groupsMember?: Group[];
  serviceHost: string;
  locale: string;
  extensionIconMap?: Record<string, string>;
  publicClientApplication?: IPublicClientApplication; //MSAL Instance for MetaOs app which is used to get tokens
  isUserEligibleForCompliantWebChat: boolean;
  shouldFocusFirstLikedPrompt?: boolean;
  /**
   * User's info such as email or displayName. Necessary for the createdByMe filter.
   */
  user?: SiteSetting["user"];
  workgroupNotificationMemberStatus: Status;
  workgroupNotificationMemberCounts?: Map<string, number>;
};

export const initialState: PromptLibraryState = {
  allPrompts: [],
  allFilteredPrompts: [],
  displayedPrompts: [],
  moreLikeThis: [],
  selectedFacets: {
    displayCategory: [],
    "products/name": [],
    jobType: [],
    createdBy: ["CreatedByAll"],
    extension: [],
    liked: [],
    workgroup: [],
  },
  selectedWorkgroupFacets: {
    workgroup: [],
  },
  status: "loading",
  filters: [],
  workgroupFilters: [],
  selectedTab: PromptTabs.AllPrompts,
  groups: undefined,
  groupPrompts: {},
  groupPromptStatus: "idle",
  displayedGroupPrompts: [],
  initSearchParams: new URLSearchParams(),
  groupHasPromptsStatus: "idle",
  groupMemberStatus: "idle",
  groupsHasPrompts: undefined,
  groupsMember: undefined,
  serviceHost: "https://copilot.cloud.microsoft/",
  locale: "",
  extensionIconMap: {},
  publicClientApplication: undefined,
  isUserEligibleForCompliantWebChat: false,
  user: undefined,
  workgroupNotificationMemberStatus: "idle",
  workgroupNotificationMemberCounts: undefined,
};

export type PromptLibraryAction =
  | {
      type: "REFRESH_DISPLAYED_PROMPTS";
    }
  | {
      type: "SET_PROMPTS";
      payload: {
        prompts: Prompt[];
        extensionIconMap?: Record<string, string>;
        status: Status;
      };
    }
  | {
      type: "SET_PROMPTS_V2";
      payload: {
        prompts: Prompt[];
        extensionIconMap?: Record<string, string>;
        status: Status;
      };
    }
  | {
      type: "SET_GROUP_PROMPTS";
      payload: {
        groupId: string;
        groupPrompts: (WorkgroupMicrosoftPrompt | WorkgroupUserPrompt)[];
      };
    }
  | {
      type: "INVALIDATE_GROUP_PROMPTS";
      payload: { groupIds: string[] };
    }
  | {
      type: "REMOVE_GROUP_PROMPT_SUCCESS";
      payload: {
        groupIds: string[];
        promptsToBeDeleted: Prompt[];
        deletedPrompts: PromptReference[];
        isUnifiedWorkgroupPromptsViewEnabled?: boolean;
      };
    }
  | {
      type: "PUBLISH_GROUP_PROMPT_SUCCESS";
      payload: {
        requestedGroupIds: string[];
        requestedPrompts: Prompt[];
        publishedPromptIds: PromptReference[];
        displayName: string;
        email?: string;
        isUnifiedWorkgroupPromptsViewEnabled?: boolean;
      };
    }
  | {
      type: "REMOVE_GROUP_PROMPT_INIT";
      payload: { groupIds: string[]; prompts: Prompt[] };
    }
  | {
      type: "REMOVE_GROUP_PROMPT_LONG";
      payload: { groupIds: string[]; prompts: Prompt[] };
    }
  | {
      type: "REMOVE_GROUP_PROMPT_FAILED";
      payload: { groupIds: string[]; prompts: Prompt[] };
    }
  | {
      type: "SET_SELECTED_FACETS";
      payload: {
        facets: Record<string, string[]>;
        isAgentPromptFilteringEnabled?: boolean;
      };
    }
  | {
      type: "LOAD_MORE";
    }
  | {
      type: "BOOKMARK_INIT";
      payload: { prompt: Prompt };
    }
  | {
      type: "BOOKMARK_SUCCEED";
      payload: { prompt: Prompt; value: boolean };
    }
  | {
      type: "BOOKMARK_FAILED";
      payload: { prompt: Prompt };
    }
  | {
      type: "MORE_LIKE_THIS";
      payload: { prompt: WebPrompt };
    }
  | {
      type: "TOGGLE_BOOKMARK_ONLY";
    }
  | {
      type: "SET_BOOKMARK_ONLY";
      payload: { selectedTab: PromptTabs; focusFirstLikedPrompt?: boolean };
    }
  | {
      type: "SET_GROUP_PROMPTS_SELECTED_FACETS_SUCCESS";
      payload: { facets: Record<string, string[]> };
    }
  | {
      type: "JUMP_TO_GROUP_PROMPTS_GROUP";
      payload: { groupId: string };
    }
  | {
      type: "SET_GROUP_PROMPTS_SELECTED_FACETS_FAILED";
      payload: { facets: Record<string, string[]> };
    }
  | {
      type: "SET_GROUP_PROMPTS_SELECTED_FACETS_INIT";
      payload: { facets: Record<string, string[]> };
    }
  | {
      type: "DELETE_UNDO";
      payload: {
        prompt: Prompt;
        promptPosition: number;
        isAgentPromptFilteringEnabled?: boolean;
      };
    }
  | {
      type: "DELETE_SUCCEED";
      payload: { prompt: Prompt; isAgentPromptFilteringEnabled?: boolean };
    }
  | {
      type: "REQUEST_ALL_GROUPS_MEMBER_INIT";
    }
  | {
      type: "REQUEST_ALL_GROUPS_MEMBER_SUCCEED";
      payload: { groups: Group[]; groupId?: string };
    }
  | {
      type: "REQUEST_ALL_GROUPS_MEMBER_FAILED";
    }
  | {
      type: "REQUEST_ALL_GROUPS_HASPROMPTS_INIT";
    }
  | {
      type: "REQUEST_ALL_GROUPS_HASPROMPTS_SUCCEED";
      payload: { groups: Group[]; groupId?: string };
    }
  | {
      type: "SET_CWC_ELIGIBILITY";
      payload: { isUserEligibleForCompliantWebChat: boolean };
    }
  | {
      type: "REQUEST_ALL_GROUPS_HASPROMPTS_FAILED";
    }
  | {
      type: "REQUEST_GROUP_MEMBER_COUNT_INIT";
    }
  | {
      type: "REQUEST_GROUP_MEMBER_COUNT_SUCCEED";
      payload: { workgroupMemberCounts?: Map<string, number> };
    }
  | {
      type: "REQUEST_GROUP_MEMBER_COUNT_FAILED";
    };

export type UseAllPromptsLibraryReducerProps = {
  serviceHost: string;
  locale: string;
  instance?: IPublicClientApplication | undefined;
  user?: SiteSetting["user"];
  selectedNavItem?: PromptTabs;
  isMetaOsV2?: boolean;
};

function useAllPromptsLibrary({
  serviceHost,
  locale,
  instance,
  user,
  selectedNavItem,
  isMetaOsV2,
}: UseAllPromptsLibraryReducerProps) {
  const [searchParams] = useSearchParams();
  const pageSize = usePageSize(PromptTabs.AllPrompts, 16);

  useEffect(() => {
    dispatch({ type: "REFRESH_DISPLAYED_PROMPTS" });
  }, [pageSize]);

  const initialStatePopulated = React.useMemo(() => {
    const isTeamsTab = localStorage.getItem("IsTeamsTab");
    let selectedTab = initialState.selectedTab;
    if (isTeamsTab === "true") {
      selectedTab = PromptTabs.GroupPrompts;
    }
    return {
      ...initialState,
      selectedTab: selectedTab,
      initSearchParams: searchParams,
      serviceHost,
      locale,
      instance,
      user,
    };
  }, [instance, locale, searchParams, serviceHost, user]);

  const filterGroupPrompts = (
    groupPrompts: Record<string, Prompt[]> | undefined,
    facets: Record<string, string[]>
  ) => {
    if (!groupPrompts) {
      return [];
    }

    let prompts: Prompt[] = [];
    for (const selectedWorkgroup of facets.workgroup) {
      if (groupPrompts[selectedWorkgroup]) {
        prompts = prompts.concat(groupPrompts[selectedWorkgroup]);
      }
    }

    return prompts;
  };

  const filterPrompts = (
    prompts: Prompt[],
    filters: Record<string, string[]>,
    selectedTab: PromptTabs,
    userEmail?: string,
    isAgentPromptFilteringEnabled?: boolean
  ) => {
    let filteredPrompts = prompts;
    const enabledFilters = { ...filters };
    switch (isMetaOsV2 ? selectedNavItem : selectedTab) {
      case PromptTabs.CombinedPrompts:
        filteredPrompts = prompts;
        break;
      case PromptTabs.AllPrompts:
        filteredPrompts = prompts.filter(
          (prompt) => isMicrosoftPrompt(prompt) && !isWorkgroupPrompt(prompt)
        );
        break;
      case PromptTabs.MicrosoftPrompts:
        filteredPrompts = prompts.filter(
          (prompt) => isMicrosoftPrompt(prompt) && !isWorkgroupPrompt(prompt)
        );
        break;
      case PromptTabs.WorkplacePrompts:
        filteredPrompts = prompts.filter(
          (prompt) => isTenantPrompt(prompt) && !isWorkgroupPrompt(prompt)
        );
        break;
      case PromptTabs.SavedPrompts:
        filteredPrompts = prompts.filter(
          (prompt) => isLikedPrompt(prompt) || isNonSharedUserPrompt(prompt)
        );
        break;
      case PromptTabs.AgentPrompts:
        filteredPrompts = prompts.filter((prompt) => isExtensionPrompt(prompt));
        break;
      case PromptTabs.GroupPrompts: {
        filteredPrompts = prompts.filter((prompt) => isWorkgroupPrompt(prompt));

        // if the prompt library is opened in a Teams channel tab, we should only display team prompts from the current team
        const isTeamsTab = !!(localStorage.getItem(IsTeamsTab) === "true");
        const teamWorkGroupId = localStorage.getItem(TeamWorkGroupId);
        if (isTeamsTab && teamWorkGroupId) {
          filteredPrompts = filteredPrompts.filter((prompt) =>
            prompt.Groups?.some((group) => group.Id === teamWorkGroupId)
          );
        }
        break;
      }
    }

    for (const [key, values] of Object.entries(enabledFilters)) {
      if (
        !isFilterEnabled(
          key as FilterName,
          selectedTab,
          isAgentPromptFilteringEnabled,
          isMetaOsV2
        )
      ) {
        continue;
      }
      const filterableValues = values
        .map((value) => {
          return value.split(FILTER_CONCATENATE_TOKEN)[0];
        })
        .filter((value) => value !== CLEAR_FILTER_TOKEN);
      switch (key) {
        case DisplayCategoryKey:
          filteredPrompts =
            filterableValues.length > 0
              ? filteredPrompts.filter((f) =>
                  filterableValues.includes(f.DisplayCategory)
                )
              : filteredPrompts;
          break;
        case "products/name":
          filteredPrompts =
            filterableValues.length > 0
              ? filteredPrompts.filter((f) =>
                  filterableValues.some((item) => f.Products.includes(item))
                )
              : filteredPrompts;
          break;
        case JobTypeKey:
          filteredPrompts =
            filterableValues.length > 0
              ? filteredPrompts.filter((f) =>
                  filterableValues.some((item) =>
                    f.Metadata?.find((m) => {
                      return m.Value === item;
                    })
                  )
                )
              : filteredPrompts;
          break;
        case ExtensionKey:
          filteredPrompts =
            filterableValues.length > 0
              ? filteredPrompts.filter(
                  (f) =>
                    filterableValues.some((item) => f.ProviderName === item) ||
                    filterableValues.some(
                      (item) => (f as UserExtensionPrompt).GptName === item
                    )
                )
              : filteredPrompts;
          break;
        case LikedKey:
          filteredPrompts =
            filterableValues.length > 0 && filterableValues[0] === "Liked"
              ? filteredPrompts.filter(isLikedPrompt)
              : filteredPrompts;
          break;
        case CreatedByKey:
          if (filterableValues.length > 0) {
            const createdByValue = filterableValues[0];
            if (createdByValue === "CreatedByMe") {
              filteredPrompts = filteredPrompts.filter(
                (prompt) =>
                  isUserPrompt(prompt) &&
                  (prompt as UserPrompt).Author?.Email === userEmail
              );
            } else if (createdByValue === "CreatedByMicrosoft") {
              filteredPrompts = filteredPrompts.filter(isMicrosoftPrompt);
            } else if (createdByValue === "CreatedByTenant") {
              filteredPrompts = filteredPrompts.filter(isTenantPrompt);
            } else if (createdByValue === "CreatedByOthers") {
              filteredPrompts = filteredPrompts.filter(isWorkgroupPrompt);
            }
          }
          break;
        case WorkgroupKey:
          filteredPrompts =
            filterableValues.length > 0
              ? filteredPrompts.filter((prompt) =>
                  filterableValues.some((groupId) =>
                    prompt.Groups?.some((g) => g.Id === groupId)
                  )
                )
              : filteredPrompts;
          break;
      }
    }
    return filteredPrompts;
  };
  /**
   * Sort product type filters by weight
   * @param filters
   */
  const sortProductTypeFilters = (filters: Filter[]): void => {
    filters.forEach((filter) => {
      if (filter.key === "products/name") {
        filter.options = filter.options.sort((a, b) => {
          const aWeight = ProductWeights[a.value as ProductType] || 0;
          const bWeight = ProductWeights[b.value as ProductType] || 0;
          return aWeight - bWeight;
        });
      }
    });
  };

  /**
   * Get similar prompts from the same workgroup
   * @param groupPrompts - all prompts in the same workgroup - deprecated, should use allPrompts
   * @param prompt - the prompt to find similar prompts for
   * @param allPrompts - list of all prompts - we will prioritize any prompts that are in this list
   * @returns - similar prompts from the same workgroup
   */
  const getMoreLikeThisFromTheSameGroup = (
    groupPrompts: Prompt[] | undefined,
    prompt: Prompt,
    allPrompts: Prompt[]
  ): WebPrompt[] => {
    const promptsFromTheSameGroup = allPrompts?.filter(
      (p) =>
        p.Id !== prompt.Id &&
        p.Groups?.some((group) => prompt.Groups?.some((g) => g.Id === group.Id))
    );
    if (promptsFromTheSameGroup.length > 0) {
      return promptsFromTheSameGroup.slice(0, 2) as WebPrompt[];
    }

    return groupPrompts
      ?.filter((x) => x.Id !== prompt.Id)
      .slice(0, 2) as WebPrompt[];
  };

  /**
   * Get similar prompts from the same display category
   * @param allPrompts - all prompts
   * @param prompt - the prompt to find similar prompts for
   * @returns - similar prompts from the same display category
   */
  const getMoreLikeThisFromTheSameCategory = (
    allPrompts: Prompt[],
    prompt: Prompt
  ): WebPrompt[] => {
    return allPrompts
      .filter(
        (x) =>
          x.DisplayCategory === prompt.DisplayCategory && x.Id !== prompt.Id
      )
      .slice(0, 2) as WebPrompt[];
  };

  const reducer = (
    state: PromptLibraryState,
    action: PromptLibraryAction
  ): PromptLibraryState => {
    const createWorkgroupFilter = (groups: Group[]): Filter[] => {
      const options: FilterOption[] = [];
      groups
        ?.filter((group) => group.HasPrompts && group.IsMember)
        .map((group) => {
          const image = group.AvatarUrl ? { src: group.AvatarUrl } : undefined;

          const filterOption: FilterOption = {
            value: group.Id,
            displayLabel: group.DisplayName,
            badge: (
              <Avatar
                name={group.DisplayName}
                shape="square"
                image={image}
                color="colorful"
                size={20}
              />
            ),
          };
          options.push(filterOption);
        });

      const workgroupFilters: Filter[] = [
        {
          key: "workgroup",
          options,
          type: "combobox",
          isSingleSelect: true,
          isOverflowItemsFrom10: true,
          isLogValuesInTelemetry: false,
          width: "wide",
        },
      ];

      return workgroupFilters;
    };

    const createWorkgroupFilterFromPrompts = (
      groups: Group[],
      prompts: Prompt[]
    ): Filter => {
      const workgroupIds = new Set<string>();
      prompts.forEach((prompt) => {
        if (prompt.Groups) {
          prompt.Groups.forEach((group) => {
            workgroupIds.add(group.Id);
          });
        }
      });

      const workgroupFilter: Filter = {
        key: WorkgroupKey,
        options: groups
          .filter((group) => workgroupIds.has(group.Id))
          .map((group) => {
            const image = group.AvatarUrl
              ? { src: group.AvatarUrl }
              : undefined;

            return {
              value: group.Id,
              displayLabel: group.DisplayName,
              badge: (
                <Avatar
                  name={group.DisplayName}
                  shape="square"
                  image={image}
                  color="colorful"
                  size={20}
                />
              ),
            };
          }),
        type: "combobox",
        isSingleSelect: false,
        isOverflowItemsFrom10: true,
        isLogValuesInTelemetry: false,
        width: "wide",
      };

      return workgroupFilter;
    };

    switch (action.type) {
      case "SET_GROUP_PROMPTS": {
        const groupPrompts: Record<
          string,
          (WorkgroupMicrosoftPrompt | WorkgroupUserPrompt)[]
        > = { ...state.groupPrompts };
        groupPrompts[action.payload.groupId] = action.payload.groupPrompts;

        return {
          ...state,
          groupPromptStatus: "fulfilled",
          groupPrompts,
          displayedGroupPrompts: filterGroupPrompts(
            groupPrompts,
            state.selectedWorkgroupFacets
          ),
        };
      }
      case "INVALIDATE_GROUP_PROMPTS": {
        const groupPrompts: Record<
          string,
          (WorkgroupMicrosoftPrompt | WorkgroupUserPrompt)[]
        > = { ...state.groupPrompts };
        action.payload.groupIds.forEach((groupId) => {
          delete groupPrompts[groupId];
        });
        return {
          ...state,
          groupPrompts,
        };
      }
      case "REMOVE_GROUP_PROMPT_SUCCESS": {
        action.payload.promptsToBeDeleted.forEach((prompt) => {
          prompt.RemovingState = "false";
        });

        const groupPrompts: Record<
          string,
          (WorkgroupMicrosoftPrompt | WorkgroupUserPrompt)[]
        > = { ...state.groupPrompts };
        const deletedPromptSet = new Set(
          action.payload.deletedPrompts.map((prompt) => prompt.PromptId)
        );
        action.payload.groupIds.forEach((groupId) => {
          groupPrompts[groupId] = groupPrompts[groupId]?.filter(
            (prompt) => !deletedPromptSet.has(prompt.PromptId)
          );
        });

        let allPrompts = state.allPrompts;
        if (action.payload.isUnifiedWorkgroupPromptsViewEnabled) {
          allPrompts = state.allPrompts?.filter(
            (prompt) => !deletedPromptSet.has(prompt.PromptId)
          );
        }

        const allFilteredPrompts = filterPrompts(
          allPrompts,
          state.selectedFacets,
          state.selectedTab,
          state.user?.email
        );

        return {
          ...state,
          groupPrompts,
          displayedGroupPrompts: filterGroupPrompts(
            groupPrompts,
            state.selectedWorkgroupFacets
          ),
          allPrompts,
          allFilteredPrompts,
          displayedPrompts: allFilteredPrompts.slice(
            0,
            state.displayedPrompts.length
          ),
        };
      }
      case "PUBLISH_GROUP_PROMPT_SUCCESS": {
        const groupPrompts: Record<
          string,
          (WorkgroupMicrosoftPrompt | WorkgroupUserPrompt)[]
        > = { ...state.groupPrompts };
        const allPrompts = state.allPrompts ?? [];
        const newGroups = state.groups ? [...state.groups] : [];
        const newGroupsMember = state.groupsMember
          ? [...state.groupsMember]
          : [];
        let promptIndex = 0;
        const groupHasPrompts =
          (state.groupsHasPrompts && [...state.groupsHasPrompts]) || [];
        action.payload.requestedPrompts.forEach((prompt) => {
          action.payload.requestedGroupIds.forEach((groupId) => {
            const sharedPrompt: MicrosoftPrompt | UserPrompt = JSON.parse(
              JSON.stringify(prompt)
            );
            sharedPrompt.Groups = sharedPrompt.Groups || [];
            const group = state.groups?.find((group) => group.Id === groupId);
            if (group) {
              // Mark the prompt shared with the group
              sharedPrompt.Groups.push(group);

              // mark the group as having prompts, so it lights up in the UX
              if (!group.HasPrompts) {
                groupHasPrompts?.push(group);
                group.HasPrompts = true;
              }
            }
            const id = action.payload.publishedPromptIds[promptIndex].PromptId;
            const parentId = prompt.Id;
            const promptId = id.split("_")[0];
            // If we fetched the group then fill the prompt in.
            // If we did not fetch the group yet, it will be fetched when accessed
            if (groupPrompts[groupId]) {
              groupPrompts[groupId].splice(0, 0, {
                ...sharedPrompt,
                Id: id,
                PromptId: promptId,
                ParentPromptId: parentId,
                Publisher: {
                  Name: action.payload.displayName,
                  Address: action.payload.email,
                },
              });
            }
            if (action.payload.isUnifiedWorkgroupPromptsViewEnabled) {
              allPrompts.splice(0, 0, {
                ...sharedPrompt,
                Id: id,
                PromptId: promptId,
                ParentPromptId: parentId,
                Publisher: {
                  Name: action.payload.displayName,
                  Address: action.payload.email,
                },
                UserActivity: {
                  Favorite: {
                    IsFavorite: false,
                  },
                },
              });
            }
            promptIndex++;
          });
        });

        return {
          ...state,
          groupPrompts,
          allPrompts,
          displayedGroupPrompts: filterGroupPrompts(
            groupPrompts,
            state.selectedWorkgroupFacets
          ),
          groupsHasPrompts: groupHasPrompts,
          groups: newGroups,
          groupsMember: newGroupsMember,
          workgroupFilters: createWorkgroupFilter(newGroups),
          filters: [
            ...state.filters.filter((filter) => filter.key !== "workgroup"),
            createWorkgroupFilterFromPrompts(newGroups, allPrompts),
          ],
        };
      }
      case "REMOVE_GROUP_PROMPT_INIT": {
        action.payload.prompts.forEach((prompt) => {
          prompt.RemovingState = "true";
        });

        return {
          ...state,
        };
      }
      case "REMOVE_GROUP_PROMPT_LONG": {
        action.payload.prompts.forEach((prompt) => {
          if (prompt.RemovingState === "true") {
            prompt.RemovingState = "long";
          }
        });

        return {
          ...state,
        };
      }
      case "REMOVE_GROUP_PROMPT_FAILED": {
        action.payload.prompts.forEach((prompt) => {
          prompt.RemovingState = "false";
        });

        return {
          ...state,
        };
      }
      case "SET_PROMPTS": {
        // we always want to display the user-authored prompts before Microsoft created ones
        const userAuthoredPrompts = action.payload.prompts.filter(isUserPrompt);
        const nonUserAuthoredPrompts = action.payload.prompts.filter(
          (prompt) => !isUserPrompt(prompt)
        );
        const allPrompts = [...userAuthoredPrompts, ...nonUserAuthoredPrompts];

        const filteredPrompts = filterPrompts(
          allPrompts,
          state.selectedFacets,
          state.selectedTab,
          state.user?.email
        );
        const displayedPrompts = filteredPrompts.slice(0, pageSize);

        sendClientLayoutEvent(
          [...displayedPrompts],
          "Vertical",
          state?.publicClientApplication
        );

        // I can't put createdByFilter inside the buildFilters helper function yet because WEPO endpoints haven't confirm
        // that they want the bookmark tab yet which is where this filter would show up. So if I put it inside buildFilters,
        // it would surface createdBy filter and that's not a good surprise.
        const createdByFilter: Filter = {
          key: "createdBy",
          isSingleSelect: true,
          options: [
            { value: "CreatedByAll" },
            ...(state.user?.email ? [{ value: "CreatedByMe" }] : []),
            { value: "CreatedByMicrosoft" },
          ],
          type: "dropdown",
          isLogValuesInTelemetry: true,
        };

        const workgroupFilter = createWorkgroupFilterFromPrompts(
          state.groups || [],
          allPrompts
        );

        const filters: Filter[] = [
          createdByFilter,
          workgroupFilter,
          ...buildFilters(allPrompts),
        ];
        sortProductTypeFilters(filters);

        return {
          ...state,
          allPrompts: [...allPrompts],
          allFilteredPrompts: [...filteredPrompts],
          displayedPrompts,
          filters: filters,
          selectedFacets: deserializeFilters(state.initSearchParams, filters),
          hasMore: allPrompts.length > pageSize,
          status: action.payload.status,
          hasBookmarkedPrompts: allPrompts.some(
            (x) => x.UserActivity.Favorite.IsFavorite || x.Origin === "User"
          ),
          extensionIconMap: action.payload.extensionIconMap ?? {},
        };
      }
      case "SET_PROMPTS_V2": {
        const allPrompts = action.payload.prompts;
        const filteredPrompts = filterPrompts(
          allPrompts,
          state.selectedFacets,
          state.selectedTab,
          state.user?.email
        );
        const displayedPrompts = filteredPrompts.slice(0, pageSize);

        sendClientLayoutEvent(
          [...allPrompts],
          "Vertical",
          state?.publicClientApplication
        );

        const aaa = state.allPrompts.some(isTenantPrompt);
        console.log(aaa);

        // I can't put createdByFilter inside the buildFilters helper function yet because WEPO endpoints haven't confirm
        // that they want the bookmark tab yet which is where this filter would show up. So if I put it inside buildFilters,
        // it would surface createdBy filter and that's not a good surprise.
        const createdByFilter: Filter = {
          key: "createdBy",
          isSingleSelect: true,
          options: [
            { value: "CreatedByAll" },
            ...(allPrompts.some(isTenantPrompt)
              ? [{ value: "CreatedByTenant" }]
              : []),
            ...(allPrompts.some(isWorkgroupPrompt)
              ? [{ value: "CreatedByOthers" }]
              : []),
            ...(allPrompts.some(isUserPrompt)
              ? [{ value: "CreatedByMe" }]
              : []),
            { value: "CreatedByMicrosoft" },
          ],
          type: "dropdown",
          isLogValuesInTelemetry: true,
        };

        const workgroupFilter = createWorkgroupFilterFromPrompts(
          state.groups || [],
          allPrompts
        );

        const filters: Filter[] = [
          createdByFilter,
          workgroupFilter,
          ...buildFilters(
            allPrompts,
            undefined,
            undefined,
            undefined,
            isMetaOsV2
          ),
        ];
        sortProductTypeFilters(filters);

        return {
          ...state,
          allPrompts: [...allPrompts],
          allFilteredPrompts: [...filteredPrompts],
          displayedPrompts,
          filters: filters,
          selectedFacets: deserializeFilters(state.initSearchParams, filters),
          hasMore: allPrompts.length > pageSize,
          status: action.payload.status,
          hasBookmarkedPrompts: allPrompts.some(
            (x) => x.UserActivity.Favorite.IsFavorite || x.Origin === "User"
          ),
          extensionIconMap: action.payload.extensionIconMap ?? {},
        };
      }
      case "SET_SELECTED_FACETS": {
        const filteredPrompts = filterPrompts(
          state.allPrompts,
          action.payload.facets,
          state.selectedTab,
          state.user?.email,
          action.payload.isAgentPromptFilteringEnabled
        );
        return {
          ...state,
          selectedFacets: action.payload.facets,
          allFilteredPrompts: filteredPrompts,
          displayedPrompts: filteredPrompts.slice(0, pageSize),
          hasMore: filteredPrompts.length > pageSize,
        };
      }
      case "LOAD_MORE": {
        const newDisplayedPrompts = state.allFilteredPrompts.slice(
          0,
          state.displayedPrompts.length + pageSize
        );

        sendClientLayoutEvent(
          [...newDisplayedPrompts],
          "Vertical",
          state?.publicClientApplication
        );

        return {
          ...state,
          displayedPrompts: newDisplayedPrompts,
          hasMore: newDisplayedPrompts.length < state.allFilteredPrompts.length,
        };
      }
      case "REFRESH_DISPLAYED_PROMPTS": {
        return {
          ...state,
          displayedPrompts: state.allFilteredPrompts.slice(0, pageSize),
          hasMore: pageSize < state.allFilteredPrompts.length,
        };
      }
      case "BOOKMARK_INIT":
        action.payload.prompt.IsBookmarking = true;
        return {
          ...state,
          allPrompts: [...state.allPrompts],
          displayedPrompts: [...state.displayedPrompts],
        };
      case "BOOKMARK_SUCCEED": {
        action.payload.prompt.IsBookmarking = false;
        action.payload.prompt.UserActivity.Favorite.IsFavorite =
          action.payload.value;
        const filteredPrompts = filterPrompts(
          state.allPrompts,
          state.selectedFacets,
          state.selectedTab,
          state.user?.email
        );
        return {
          ...state,
          allPrompts: [...state.allPrompts],
          allFilteredPrompts: filteredPrompts,
          displayedPrompts:
            state.selectedTab === PromptTabs.SavedPrompts
              ? state.displayedPrompts.filter(
                  (prompt) =>
                    prompt.UserActivity.Favorite.IsFavorite ||
                    isUserPrompt(prompt)
                )
              : [...state.displayedPrompts],
          hasBookmarkedPrompts: state.allPrompts.some(
            (prompt) =>
              prompt.UserActivity.Favorite.IsFavorite || isUserPrompt(prompt)
          ),
        };
      }
      case "BOOKMARK_FAILED":
        action.payload.prompt.IsBookmarking = false;
        return {
          ...state,
          allPrompts: [...state.allPrompts],
          displayedPrompts: [...state.displayedPrompts],
        };
      case "MORE_LIKE_THIS": {
        const groupPrompts = filterGroupPrompts(
          state.groupPrompts,
          state.selectedWorkgroupFacets
        );
        const moreLikeThis = action.payload.prompt.Groups
          ? getMoreLikeThisFromTheSameGroup(
              groupPrompts,
              action.payload.prompt,
              state.allPrompts
            )
          : getMoreLikeThisFromTheSameCategory(
              state.allPrompts,
              action.payload.prompt
            );
        return {
          ...state,
          moreLikeThis: moreLikeThis ?? [],
        };
      }
      case "TOGGLE_BOOKMARK_ONLY": {
        const toggleTab =
          state.selectedTab === PromptTabs.SavedPrompts
            ? PromptTabs.AllPrompts
            : PromptTabs.SavedPrompts;
        const filteredPrompts = filterPrompts(
          state.allPrompts,
          state.selectedFacets,
          toggleTab,
          state.user?.email
        );
        return {
          ...state,
          selectedTab: toggleTab,
          allFilteredPrompts: filteredPrompts,
          displayedPrompts: filteredPrompts.slice(0, pageSize),
          hasMore: filteredPrompts.length > pageSize,
        };
      }
      case "SET_BOOKMARK_ONLY": {
        const filteredPrompts = filterPrompts(
          state.allPrompts,
          state.selectedFacets,
          action.payload.selectedTab,
          state.user?.email
        );
        return {
          ...state,
          selectedTab: action.payload.selectedTab,
          allFilteredPrompts: filteredPrompts,
          displayedPrompts: filteredPrompts.slice(0, pageSize),
          hasMore: filteredPrompts.length > pageSize,
          displayedGroupPrompts: filterGroupPrompts(
            state.groupPrompts,
            state.selectedWorkgroupFacets
          ),
          shouldFocusFirstLikedPrompt:
            action.payload.focusFirstLikedPrompt || false,
        };
      }
      case "SET_GROUP_PROMPTS_SELECTED_FACETS_INIT": {
        return {
          ...state,
          selectedWorkgroupFacets: action.payload.facets,
          groupPromptStatus: "loading",
          displayedGroupPrompts: filterGroupPrompts(
            state.groupPrompts,
            action.payload.facets
          ),
        };
      }
      case "SET_GROUP_PROMPTS_SELECTED_FACETS_SUCCESS": {
        return {
          ...state,
          selectedWorkgroupFacets: action.payload.facets,
          displayedGroupPrompts: filterGroupPrompts(
            state.groupPrompts,
            action.payload.facets
          ),
          groupPromptStatus: "fulfilled",
        };
      }
      case "JUMP_TO_GROUP_PROMPTS_GROUP": {
        const selectedFacets = { workgroup: [action.payload.groupId] };
        const allFilteredPrompts = filterPrompts(
          state.allPrompts,
          selectedFacets,
          PromptTabs.GroupPrompts,
          state.user?.email
        );
        return {
          ...state,
          selectedWorkgroupFacets: { workgroup: [action.payload.groupId] },
          selectedFacets,
          selectedTab: PromptTabs.GroupPrompts,
          displayedGroupPrompts: filterGroupPrompts(state.groupPrompts, {
            workgroup: [action.payload.groupId],
          }),
          displayedPrompts: allFilteredPrompts.slice(0, pageSize),
          hasMore: allFilteredPrompts.length > pageSize,
          allFilteredPrompts,
        };
      }
      case "SET_GROUP_PROMPTS_SELECTED_FACETS_FAILED": {
        return {
          ...state,
          selectedWorkgroupFacets: action.payload.facets,
          displayedGroupPrompts: [],
          groupPromptStatus: "failed",
        };
      }
      case "DELETE_UNDO": {
        const allPrompts = [...state.allPrompts];

        // make sure the prompt is not already in the list
        if (
          allPrompts.findIndex(
            (prompt) => prompt.Id === action.payload.prompt.Id
          ) === -1
        ) {
          allPrompts.splice(
            action.payload.promptPosition,
            0,
            action.payload.prompt
          );
        }

        const allFilteredPrompts = filterPrompts(
          allPrompts,
          state.selectedFacets,
          state.selectedTab,
          state.user?.email,
          action.payload.isAgentPromptFilteringEnabled
        );
        const displayedPrompts = allFilteredPrompts.slice(
          0,
          state.displayedPrompts.length + 1
        );

        return {
          ...state,
          allPrompts,
          allFilteredPrompts,
          displayedPrompts,
          hasMore: allFilteredPrompts.length > pageSize,
        };
      }
      case "DELETE_SUCCEED": {
        const allPrompts = [...state.allPrompts].filter(
          (prompt) => prompt.Id !== action.payload.prompt.Id
        );
        const allFilteredPrompts = filterPrompts(
          allPrompts,
          state.selectedFacets,
          state.selectedTab,
          state.user?.email,
          action.payload.isAgentPromptFilteringEnabled
        );
        const displayedPrompts = allFilteredPrompts.slice(
          0,
          state.displayedPrompts.length
        );

        return {
          ...state,
          allPrompts,
          allFilteredPrompts,
          displayedPrompts,
          hasMore: allFilteredPrompts.length > pageSize,
        };
      }
      case "REQUEST_ALL_GROUPS_MEMBER_INIT":
        return {
          ...state,
          groupMemberStatus: "loading",
        };
      case "REQUEST_ALL_GROUPS_MEMBER_SUCCEED": {
        const sortedMemberGroups: Group[] = action.payload.groups.sort(
          (a: Group, b: Group) => a.DisplayName.localeCompare(b.DisplayName)
        );

        const groups: Group[] = state.groups
          ? [...(state.groups as Group[])]
          : [];
        sortedMemberGroups.forEach((group) => {
          const existingGroup = groups.find((g) => g.Id === group.Id);
          if (!existingGroup) {
            groups.push(group);
          } else {
            existingGroup.IsMember = true;
          }
        });
        groups.forEach((group) => {
          // it's possible user lost group membership since last fetch
          if (!sortedMemberGroups.find((g) => g.Id === group.Id)) {
            group.IsMember = false;
          }
        });

        const sortedGroups =
          groups?.sort((a: Group, b: Group) =>
            a.DisplayName.localeCompare(b.DisplayName)
          ) ?? [];

        return {
          ...state,
          groupMemberStatus: "fulfilled",
          groupsMember: sortedMemberGroups,
          groups: sortedGroups,
          workgroupFilters: createWorkgroupFilter(sortedGroups),
          filters: [
            ...state.filters.filter((filter) => filter.key !== "workgroup"),
            createWorkgroupFilterFromPrompts(sortedGroups, state.allPrompts),
          ],
        };
      }
      case "REQUEST_ALL_GROUPS_MEMBER_FAILED":
        return {
          ...state,
          groupMemberStatus: "failed",
          groupsMember: undefined,
        };
      case "REQUEST_ALL_GROUPS_HASPROMPTS_INIT":
        return {
          ...state,
          groupHasPromptsStatus: "loading",
        };
      case "REQUEST_ALL_GROUPS_HASPROMPTS_SUCCEED": {
        const sortedHasPromptsGroups: Group[] = action.payload.groups.sort(
          (a: Group, b: Group) => a.DisplayName.localeCompare(b.DisplayName)
        );

        const groups: Group[] = state.groups
          ? [...(state.groups as Group[])]
          : [];
        sortedHasPromptsGroups.forEach((group) => {
          const existingGroup = groups.find((g) => g.Id === group.Id);
          if (!existingGroup) {
            groups.push(group);
          } else {
            existingGroup.HasPrompts = true;
          }
        });

        const sortedGroups =
          groups?.sort((a: Group, b: Group) =>
            a.DisplayName.localeCompare(b.DisplayName)
          ) ?? [];

        return {
          ...state,
          groupHasPromptsStatus: "fulfilled",
          groupsHasPrompts: sortedHasPromptsGroups,
          groups: sortedGroups,
          workgroupFilters: createWorkgroupFilter(sortedGroups),
          filters: [
            ...state.filters.filter((filter) => filter.key !== "workgroup"),
            createWorkgroupFilterFromPrompts(sortedGroups, state.allPrompts),
          ],
        };
      }
      case "SET_CWC_ELIGIBILITY": {
        return {
          ...state,
          isUserEligibleForCompliantWebChat:
            action.payload.isUserEligibleForCompliantWebChat,
        };
      }
      case "REQUEST_ALL_GROUPS_HASPROMPTS_FAILED":
        return {
          ...state,
          groupHasPromptsStatus: "failed",
          groupsHasPrompts: undefined,
        };
      case "REQUEST_GROUP_MEMBER_COUNT_INIT":
        return {
          ...state,
          workgroupNotificationMemberStatus: "loading",
        };
      case "REQUEST_GROUP_MEMBER_COUNT_SUCCEED": {
        let counts = state.workgroupNotificationMemberCounts;
        if (action.payload.workgroupMemberCounts) {
          counts = new Map<string, number>();

          if (state.workgroupNotificationMemberCounts) {
            for (const key of Array.from(
              state.workgroupNotificationMemberCounts.keys()
            )) {
              counts.set(
                key,
                state.workgroupNotificationMemberCounts.get(key) as number
              );
            }
          }

          for (const key of Array.from(
            action.payload.workgroupMemberCounts.keys()
          )) {
            counts.set(
              key,
              action.payload.workgroupMemberCounts.get(key) as number
            );
          }
        }

        return {
          ...state,
          workgroupNotificationMemberStatus: "fulfilled",
          workgroupNotificationMemberCounts: counts,
        };
      }
      case "REQUEST_GROUP_MEMBER_COUNT_FAILED":
        return {
          ...state,
          workgroupNotificationMemberStatus: "failed",
        };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialStatePopulated);

  return {
    state,
    dispatch,
  };
}

export default useAllPromptsLibrary;
